/**
 * FG Magnit — category bar mega menu.
 *
 *  Mouse:     pointing at a category opens its panel after a short pause (so a
 *             cursor passing over the bar doesn't flash panels); moving to
 *             another category switches instantly; the panel stays open while
 *             the cursor is anywhere over the bar or the panel, and closes a
 *             moment after it leaves. Clicking a category goes to its collection.
 *  Touch:     the first tap on a category opens its panel; a second tap on the
 *             same category goes to its collection. Tapping outside closes it.
 *  Keyboard:  Tab moves along the bar; Enter follows the link; Space or Down
 *             arrow opens the panel and moves into it; Escape closes and returns
 *             focus to the category. Leaving the menu with Tab closes it.
 */
(() => {
  if (customElements.get('fg-mega-nav')) return;

  const OPEN_DELAY = 90; // ms before the first panel opens on hover
  const CLOSE_DELAY = 220; // ms grace after the cursor leaves

  class FgMegaNav extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.fg-nav__item')];
      this.panels = new Map([...this.querySelectorAll('[data-panel-id]')].map((p) => [p.dataset.panelId, p]));
      this.wrap = this.querySelector('[data-panels]');
      this.openId = null;
      this.lastPointer = 'mouse';
      this.header = this.closest('.header-wrapper');

      this.items.forEach((li) => {
        const link = li.querySelector('.fg-nav__link');
        const id = li.dataset.panel;

        li.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          if (!id) {
            // A plain link (e.g. Offers): close any open panel promptly.
            if (this.openId) this.closeTimer = setTimeout(() => this.close(), 120);
            return;
          }
          if (this.openId) this.show(id);
          else this.openTimer = setTimeout(() => this.show(id), OPEN_DELAY);
        });
        li.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') clearTimeout(this.openTimer);
        });

        if (!id) return;
        link.addEventListener('click', (e) => {
          // Mouse users already see the panel on hover, so a click navigates.
          if (this.lastPointer === 'mouse') return;
          if (this.openId !== id) {
            e.preventDefault();
            this.show(id);
          }
        });
        link.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.show(id);
            const first = this.panels.get(id)?.querySelector('a');
            if (first) first.focus();
          }
        });
      });

      // Panel pictures are lazy-loaded while hidden; start fetching them as soon
      // as the visitor reaches the menu so they are there when a panel opens.
      const warm = () => {
        if (this.warmed) return;
        this.warmed = true;
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
      };
      this.addEventListener('pointerenter', warm, { once: true });
      this.addEventListener('focusin', warm, { once: true });
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });

      this.addEventListener('pointerdown', (e) => (this.lastPointer = e.pointerType), true);
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') clearTimeout(this.closeTimer);
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
      });
      this.addEventListener('focusout', (e) => {
        if (this.openId && !this.contains(e.relatedTarget)) this.close();
      });

      this.onKey = (e) => {
        if (e.key !== 'Escape' || !this.openId) return;
        const trigger = this.querySelector(`[aria-controls="${this.openId}"]`);
        this.close();
        if (trigger && this.contains(document.activeElement)) trigger.focus();
      };
      this.onDocPointer = (e) => {
        if (this.openId && !this.contains(e.target)) this.close();
      };
      this.onResize = () => {
        if (this.openId) this.place();
      };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('pointerdown', this.onDocPointer);
      window.addEventListener('resize', this.onResize, { passive: true });
      // Other header pop-ups (search, account, cart drawer) close the menu.
      document.addEventListener('focusin', (e) => {
        if (this.openId && !this.contains(e.target)) this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerdown', this.onDocPointer);
      window.removeEventListener('resize', this.onResize);
    }

    // The dimmed backdrop starts at the bottom of the header, wherever it is.
    place() {
      const bottom = (this.header || this).getBoundingClientRect().bottom;
      this.style.setProperty('--fg-nav-bottom', `${Math.max(0, Math.round(bottom))}px`);
    }

    show(id) {
      const panel = this.panels.get(id);
      if (!panel) return;
      if (this.openId === id) return;
      const wasOpen = !!this.openId;
      this.panels.forEach((p, pid) => {
        const on = pid === id;
        p.hidden = !on;
        p.classList.toggle('is-active', on);
      });
      this.items.forEach((li) => {
        const on = li.dataset.panel === id;
        li.classList.toggle('is-open', on);
        const a = li.querySelector('.fg-nav__link');
        if (li.dataset.panel) a.setAttribute('aria-expanded', String(on));
      });
      this.openId = id;
      this.place();
      // Switching between panels swaps content without re-running the opening
      // animation, so there is no flicker while moving along the bar.
      this.classList.toggle('is-switching', wasOpen);
      this.classList.add('is-open');
      document.documentElement.classList.add('fg-nav-open');
    }

    close() {
      if (!this.openId) return;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.openId = null;
      this.classList.remove('is-open', 'is-switching');
      document.documentElement.classList.remove('fg-nav-open');
      this.items.forEach((li) => {
        li.classList.remove('is-open');
        const a = li.querySelector('.fg-nav__link');
        if (li.dataset.panel) a.setAttribute('aria-expanded', 'false');
      });
      // Hide the panels once the fade-out has finished.
      const done = () => {
        if (!this.openId) this.panels.forEach((p) => (p.hidden = true));
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 180);
    }
  }

  customElements.define('fg-mega-nav', FgMegaNav);
})();
